import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  Settings,
  Play,
  RefreshCw,
  Eye,
  AlertCircle,
  Loader,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Clock,
} from "lucide-react";
import { Button } from "../../../components/ui/Button";
import { Modal } from "../../../components/ui/Modal";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { useToast } from "../../../hooks/useToast";
import { ToastContainer } from "../../../components/ui/Toast";
import {
  usePayrollRuns,
  useTriggerPayroll,
  useBizPayrollRuns,
  useTriggerBizPayroll,
} from "../../../hooks/usePayroll";
import { useAuth } from "../../../hooks/useAuth";
import { formatPeriod } from "../../../utils/format";
import { PayrollConfigModal } from "./PayrollConfigModal";
import type { PayrollRun, PayrollRunStatus } from "../../../lib/payrollApi";

const PAGE_SIZE = 10;

// ── Period generation ─────────────────────────────────────────────────────────

function generatePeriods(count = 24): string[] {
  const periods: string[] = [];
  const now = new Date();
  let year = now.getFullYear();
  let month = now.getMonth() + 1;
  let half: "H1" | "H2" = now.getDate() <= 15 ? "H1" : "H2";

  for (let i = 0; i < count; i++) {
    periods.push(`${year}-${String(month).padStart(2, "0")}-${half}`);
    if (half === "H2") {
      half = "H1";
    } else {
      half = "H2";
      if (month === 1) { year--; month = 12; }
      else { month--; }
    }
  }

  const seen = new Set<string>();
  return periods.filter((p) => {
    if (seen.has(p)) return false;
    seen.add(p);
    return true;
  });
}

const ALL_PERIODS = generatePeriods(6);
const CURRENT_PERIOD = ALL_PERIODS[0];

// ── Status badge ──────────────────────────────────────────────────────────────

type DisplayStatus = PayrollRunStatus | "NOT_STARTED";

function StatusBadge({ status }: { status: DisplayStatus }) {
  switch (status) {
    case "PROCESSING":
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#eff3ff] px-2.5 py-1 text-xs font-semibold text-[#2447d9]">
          <Loader className="h-3 w-3 animate-spin" />
          Processing
        </span>
      );
    case "COMPLETED":
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#f0fdf4] px-2.5 py-1 text-xs font-semibold text-[#16a34a]">
          <CheckCircle2 className="h-3 w-3" />
          Completed
        </span>
      );
    case "APPROVED":
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#eff3ff] px-2.5 py-1 text-xs font-semibold text-[#2447d9]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#2447d9]" />
          Approved
        </span>
      );
    case "FAILED":
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#fef2f2] px-2.5 py-1 text-xs font-semibold text-[#dc2626]">
          <AlertCircle className="h-3 w-3" />
          Failed
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#f1f5f9] px-2.5 py-1 text-xs font-semibold text-[#64748b]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#94a3b8]" />
          Not Started
        </span>
      );
  }
}

// ── Process Confirmation Modal ────────────────────────────────────────────────

function ProcessConfirmModal({ period, isOpen, onClose, onConfirm, isPending }: {
  period: string; isOpen: boolean; onClose: () => void; onConfirm: () => void; isPending: boolean;
}) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Process Payroll" size="sm">
      <div className="space-y-4">
        <div className="rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-4 py-3">
          <p className="text-xs text-[#94a3b8] uppercase tracking-wide font-semibold mb-0.5">Pay Period</p>
          <p className="text-base font-bold text-[#0f172a]">{formatPeriod(period)}</p>
        </div>

        <p className="text-sm text-[#64748b]">
          Payslips will be automatically computed for <strong>all active employees</strong> using
          your payroll configuration — including government deductions, allowances, loan
          installments, and attendance.
        </p>

        <p className="text-xs text-[#94a3b8] bg-[#fffbeb] border border-[#fde68a] rounded-lg px-3 py-2">
          Any existing <strong>draft</strong> payslips for this period will be overwritten.
        </p>

        <div className="flex gap-3 pt-1">
          <Button variant="secondary" onClick={onClose} fullWidth disabled={isPending}>Cancel</Button>
          <Button variant="primary" onClick={onConfirm} fullWidth disabled={isPending}>
            <Play className="h-4 w-4 mr-1.5" />
            {isPending ? "Starting…" : "Process Payroll"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AdminPayslipPage() {
  const navigate = useNavigate();
  const { toasts, toast, dismissToast } = useToast();
  const { activeBusinessId } = useAuth();
  const bizId = activeBusinessId ?? "";

  const hasBiz = !!bizId;
  const adminRunsQuery = usePayrollRuns({ enabled: !hasBiz });
  const bizRunsQuery = useBizPayrollRuns(bizId);
  const triggerAdminPayroll = useTriggerPayroll();
  const triggerBizPayroll = useTriggerBizPayroll(bizId);

  const { data: runs = [], isLoading, refetch } = hasBiz ? bizRunsQuery : adminRunsQuery;
  const triggerPayroll = hasBiz ? triggerBizPayroll : triggerAdminPayroll;

  const [showConfig, setShowConfig] = useState(false);
  const [confirmPeriod, setConfirmPeriod] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  const runsMap = useMemo(() => {
    const map = new Map<string, PayrollRun>();
    for (const run of runs) map.set(run.period, run);
    return map;
  }, [runs]);

  const hasProcessing = runs.some((r) => r.status === "PROCESSING");
  useEffect(() => {
    if (!hasProcessing) return;
    const timer = setInterval(() => refetch(), 5000);
    return () => clearInterval(timer);
  }, [hasProcessing, refetch]);

  const totalPages = Math.ceil(ALL_PERIODS.length / PAGE_SIZE);
  const visiblePeriods = ALL_PERIODS.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Current period context for the header card
  const currentRun = runsMap.get(CURRENT_PERIOD);
  const currentStatus: DisplayStatus = currentRun?.status ?? "NOT_STARTED";

  // Most recent completed/approved run (for context tile)
  const mostRecentRun = useMemo(() => {
    return ALL_PERIODS.map((p) => runsMap.get(p)).find(
      (r) => r && (r.status === "COMPLETED" || r.status === "APPROVED"),
    );
  }, [runsMap]);

  const handleProcess = async () => {
    if (!confirmPeriod) return;
    const period = confirmPeriod;
    try {
      await triggerPayroll.mutateAsync(period);
      setConfirmPeriod(null);
      toast.success(`Payroll processing started for ${formatPeriod(period)}.`);
    } catch (err: unknown) {
      setConfirmPeriod(null);
      const msg = err instanceof Error ? err.message : "";
      toast.error(
        msg.toLowerCase().includes("already")
          ? "Payroll is already being processed for this period."
          : "Failed to start payroll processing.",
      );
    }
  };

  const resultsPath = (period: string) =>
    hasBiz
      ? `/admin/payslip/${encodeURIComponent(bizId)}/${encodeURIComponent(period)}/results`
      : `/admin/payslip/${encodeURIComponent(period)}/results`;

  return (
    <div className="space-y-5">
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      {/* ── Header card ───────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-[#e2e8f0] bg-white px-5 py-4 shadow-sm">
        {/* Title + config */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#2447d9]">
              <FileText className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-[#0f172a]">Process Payslip</h1>
              <p className="text-xs text-[#64748b]">Batch payroll processing per pay period</p>
            </div>
          </div>
          <button
            onClick={() => setShowConfig(true)}
            title="Payroll Configuration"
            className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] px-3 py-1.5 text-xs font-medium text-[#64748b] hover:border-[#2447d9] hover:text-[#2447d9] hover:bg-[#eff3ff] transition-colors"
          >
            <Settings className="h-3.5 w-3.5" />
            Config
          </button>
        </div>

        {/* Current period + most recent run */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Current period tile */}
          <div className="rounded-xl border border-[#c7d2fe] bg-[#eff3ff] px-4 py-3">
            <p className="text-[10px] font-semibold text-[#4f46e5] uppercase tracking-wide mb-1">
              Current Period
            </p>
            <p className="text-sm font-bold text-[#0f172a]">{formatPeriod(CURRENT_PERIOD)}</p>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {isLoading ? (
                <div className="h-6 w-20 rounded bg-[#c7d2fe] animate-pulse" />
              ) : (
                <>
                  <StatusBadge status={currentStatus} />
                  {currentStatus === "NOT_STARTED" && (
                    <button
                      onClick={() => setConfirmPeriod(CURRENT_PERIOD)}
                      className="flex items-center gap-1 rounded-lg bg-[#2447d9] px-2.5 py-1 text-xs font-medium text-white hover:bg-[#1e3a8a] transition-colors"
                    >
                      <Play className="h-3 w-3" />
                      Process
                    </button>
                  )}
                  {currentStatus === "COMPLETED" && (
                    <button
                      onClick={() => navigate(resultsPath(CURRENT_PERIOD))}
                      className="flex items-center gap-1 rounded-lg bg-[#2447d9] px-2.5 py-1 text-xs font-medium text-white hover:bg-[#1e3a8a] transition-colors"
                    >
                      <Eye className="h-3 w-3" />
                      View & Approve
                    </button>
                  )}
                  {currentStatus === "APPROVED" && (
                    <button
                      onClick={() => navigate(resultsPath(CURRENT_PERIOD))}
                      className="flex items-center gap-1 rounded-lg border border-[#2447d9] px-2.5 py-1 text-xs font-medium text-[#2447d9] hover:bg-[#eff3ff] transition-colors"
                    >
                      <Eye className="h-3 w-3" />
                      View
                    </button>
                  )}
                  {currentStatus === "PROCESSING" && (
                    <span className="text-xs text-[#2447d9] font-medium animate-pulse">
                      Computing payslips…
                    </span>
                  )}
                  {currentStatus === "FAILED" && (
                    <button
                      onClick={() => setConfirmPeriod(CURRENT_PERIOD)}
                      className="flex items-center gap-1 rounded-lg bg-[#dc2626] px-2.5 py-1 text-xs font-medium text-white hover:bg-[#b91c1c] transition-colors"
                    >
                      <RefreshCw className="h-3 w-3" />
                      Retry
                    </button>
                  )}
                </>
              )}
            </div>
            {currentRun?.triggeredBy && (
              <p className="text-[10px] text-[#6366f1] mt-1.5">Processed by {currentRun.triggeredBy}</p>
            )}
          </div>

          {/* Most recent run tile */}
          <div className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-4 py-3">
            <p className="text-[10px] font-semibold text-[#94a3b8] uppercase tracking-wide mb-1">
              Most Recent Run
            </p>
            {isLoading ? (
              <div className="space-y-1.5">
                <div className="h-4 w-28 rounded bg-[#e2e8f0] animate-pulse" />
                <div className="h-3 w-20 rounded bg-[#e2e8f0] animate-pulse" />
              </div>
            ) : mostRecentRun ? (
              <>
                <p className="text-sm font-bold text-[#0f172a]">{formatPeriod(mostRecentRun.period)}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <StatusBadge status={mostRecentRun.status} />
                  {mostRecentRun.totalEmployees > 0 && (
                    <span className="text-xs text-[#64748b]">
                      {mostRecentRun.processedCount}/{mostRecentRun.totalEmployees} emp
                    </span>
                  )}
                </div>
                {mostRecentRun.triggeredBy && (
                  <p className="text-[10px] text-[#94a3b8] mt-1">By {mostRecentRun.triggeredBy}</p>
                )}
              </>
            ) : (
              <div className="flex items-center gap-2 mt-1">
                <Clock className="h-4 w-4 text-[#cbd5e1]" />
                <p className="text-sm text-[#94a3b8]">No runs yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Pay Periods table ─────────────────────────────────────────────── */}
      <div className="rounded-xl border border-[#e2e8f0] bg-white shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-[#e2e8f0] flex items-center justify-between">
          <p className="text-sm font-semibold text-[#0f172a]">Pay Periods</p>
          <button
            onClick={() => refetch()}
            title="Refresh"
            className="flex h-7 w-7 items-center justify-center rounded-lg text-[#94a3b8] hover:text-[#64748b] hover:bg-[#f1f5f9] transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12"><LoadingSpinner /></div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                    {["Pay Period", "Status", "Employees", "Actions"].map((h) => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f1f5f9]">
                  {visiblePeriods.map((period) => {
                    const run = runsMap.get(period);
                    const status: DisplayStatus = run?.status ?? "NOT_STARTED";
                    const isCurrent = period === CURRENT_PERIOD && page === 0;

                    return (
                      <tr
                        key={period}
                        className={`transition-colors ${
                          isCurrent
                            ? "bg-[#fafbff] border-l-4 border-l-[#2447d9]"
                            : "bg-white hover:bg-[#f8fafc]"
                        }`}
                      >
                        <td className="px-5 py-3.5 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className={`font-medium ${isCurrent ? "text-[#2447d9]" : "text-[#0f172a]"}`}>
                              {formatPeriod(period)}
                            </span>
                            {isCurrent && (
                              <span className="inline-flex items-center rounded-full bg-[#2447d9] px-2 py-0.5 text-[10px] font-semibold text-white">
                                Current
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-3.5 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <StatusBadge status={status} />
                            {run?.triggeredBy && (
                              <span className="text-[10px] text-[#94a3b8] hidden sm:inline">
                                {run.approvedBy ? `✓ ${run.approvedBy}` : run.triggeredBy}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-3.5 whitespace-nowrap text-[#64748b]">
                          {run && run.totalEmployees > 0 ? (
                            <span>
                              {run.processedCount}/{run.totalEmployees}
                              {run.failedCount && run.failedCount > 0 ? (
                                <span className="ml-1.5 text-xs text-[#dc2626]">({run.failedCount} failed)</span>
                              ) : null}
                            </span>
                          ) : "—"}
                        </td>
                        <td className="px-5 py-3.5 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {status === "NOT_STARTED" && (
                              <button
                                onClick={() => setConfirmPeriod(period)}
                                className="flex items-center gap-1 rounded-lg bg-[#2447d9] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#1e3a8a] transition-colors"
                              >
                                <Play className="h-3 w-3" />
                                Process
                              </button>
                            )}
                            {status === "PROCESSING" && (
                              <span className="text-xs text-[#2447d9] font-medium">Processing…</span>
                            )}
                            {status === "COMPLETED" && (
                              <>
                                <button
                                  onClick={() => navigate(resultsPath(period))}
                                  className="flex items-center gap-1 rounded-lg bg-[#2447d9] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#1e3a8a] transition-colors"
                                >
                                  <Eye className="h-3 w-3" />
                                  View & Approve
                                </button>
                                <button
                                  onClick={() => setConfirmPeriod(period)}
                                  title="Re-process"
                                  className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#e2e8f0] text-[#94a3b8] hover:bg-[#f1f5f9] hover:text-[#374151] transition-colors"
                                >
                                  <RefreshCw className="h-3.5 w-3.5" />
                                </button>
                              </>
                            )}
                            {status === "APPROVED" && (
                              <button
                                onClick={() => navigate(resultsPath(period))}
                                className="flex items-center gap-1 rounded-lg border border-[#e2e8f0] px-3 py-1.5 text-xs font-medium text-[#374151] hover:bg-[#f8fafc] transition-colors"
                              >
                                <Eye className="h-3 w-3" />
                                View
                              </button>
                            )}
                            {status === "FAILED" && (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => setConfirmPeriod(period)}
                                  className="flex items-center gap-1 rounded-lg border border-[#fecaca] bg-[#fef2f2] px-3 py-1.5 text-xs font-medium text-[#dc2626] hover:bg-[#fee2e2] transition-colors"
                                >
                                  <RefreshCw className="h-3 w-3" />
                                  Retry
                                </button>
                                {run?.errorMessage && (
                                  <span className="text-xs text-[#dc2626] truncate max-w-[160px]" title={run.errorMessage}>
                                    {run.errorMessage}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-[#e2e8f0]">
                <span className="text-xs text-[#64748b]">Page {page + 1} of {totalPages}</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="flex items-center gap-1 rounded-lg border border-[#e2e8f0] bg-white px-3 py-1.5 text-xs font-medium text-[#64748b] disabled:opacity-40 hover:bg-[#f8fafc] transition-colors"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                    Prev
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page === totalPages - 1}
                    className="flex items-center gap-1 rounded-lg border border-[#e2e8f0] bg-white px-3 py-1.5 text-xs font-medium text-[#64748b] disabled:opacity-40 hover:bg-[#f8fafc] transition-colors"
                  >
                    Next
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      <PayrollConfigModal
        isOpen={showConfig}
        onClose={() => setShowConfig(false)}
        businessId={bizId || undefined}
      />

      {confirmPeriod && (
        <ProcessConfirmModal
          period={confirmPeriod}
          isOpen={!!confirmPeriod}
          onClose={() => setConfirmPeriod(null)}
          onConfirm={handleProcess}
          isPending={triggerPayroll.isPending}
        />
      )}
    </div>
  );
}
